// src/data/blogsData.js
// Central blog data store. Add new blog posts here to automatically populate
// the /blog listing page and individual post routes.

import { HTML_BLOGS } from "./blogsDataIntoHtml";

export const BLOGS = [
  {
    slug: "dubai-real-estate-iran-us-war-2026",
    title: "Dubai Real Estate Market & the Iran-US War: What Investors Need to Know Right Now (2026)",
    excerpt:
      "Q1 2026 DLD data shows AED 252B in transactions — up 31% YoY. Here's what is actually happening to Dubai property prices during the conflict, and what to do next.",
    category: "Market Analysis",
    tag: "Market Analysis · May 2026",
    author: "Nova Capital Real Estate",
    datePublished: "2026-05-19T10:00:00+04:00",
    dateDisplay: "19 May 2026",
    readTime: "7 min read",
    image: "/images/blog/dubai-real-estate-iran-war-2026.jpg",
    imageAlt:
      "Dubai skyline with real estate market data overlay showing Q1 2026 transaction figures during Iran-US conflict",
    imageCaption:
      "Dubai's property market recorded AED 252 billion in Q1 2026 transactions despite regional tensions (DLD, 2026)",
    canonical: "https://www.novacapitaldxb.com/blog/dubai-real-estate-iran-us-war-2026",
    metaDescription:
      "Is Dubai property still a safe investment during the Iran-US conflict? Q1 2026 DLD data, market impact analysis, and what smart investors are doing right now. Zero commission advice from Nova Capital.",
    keywords:
      "Dubai real estate Iran war, Dubai property market 2026, buy property Dubai during conflict, off-plan investment Dubai 2026, Dubai property safe investment, Nova Capital Dubai",
    ogDescription:
      "Q1 2026 DLD data shows AED 252B in transactions — up 31% YoY. Here's what is actually happening to Dubai property prices during the conflict, and what to do next.",

    // JSON-LD structured data
    faqSchema: [
      {
        question: "Is Dubai real estate safe to invest in during the Iran-US war?",
        answer:
          "Yes. Q1 2026 DLD data shows AED 252 billion in transaction volume, up 31% year-on-year, with over 29,000 new investors entering the market. While short-term sentiment dipped, transaction values continued rising and buyer viewing activity recovered 75% within 10 days of initial conflict escalation.",
      },
      {
        question: "Have Dubai property prices dropped because of the Iran war?",
        answer:
          "Average home prices dipped modestly — approximately 4–5% — in the immediate weeks following escalation. However, the median price per sqft has risen to AED 1,818 in 2026 from AED 935 in 2020, and analysts expect this to be a temporary correction rather than a structural decline.",
      },
      {
        question: "Should I wait or buy Dubai property now during the conflict?",
        answer:
          "Many experienced investors are using this window as a buying opportunity. Large capital has not exited the market — Q1 2026 saw AED 148 billion in foreign investment, up 26% YoY. If tensions stabilise, early buyers typically benefit from pre-recovery pricing.",
      },
    ],

    // Key stats strip
    stats: [
      { num: "AED 252B", label: "Q1 2026 transaction volume" },
      { num: "+31%", label: "Year-on-year growth" },
      { num: "60,303", label: "Total Q1 transactions" },
      { num: "6–8.5%", label: "Rental yields in key communities" },
      { num: "29,000+", label: "New investors in Q1 2026" },
    ],

    // Article body as structured sections
    sections: [
      {
        type: "paragraph",
        content:
          "The headlines have been alarming. Drone debris over Palm Jumeirah. A real estate stock index that shed over 15% in a single week. International buyers pressing pause. If you are holding Dubai property, considering a purchase, or weighing your options as an NRI or international investor, you need the actual data — not panic, not spin.",
      },
      {
        type: "paragraph",
        content:
          "This is a market analysis written in May 2026 using the most current Dubai Land Department (DLD) figures, S&P Global ratings, and on-the-ground transaction data. It will tell you what has happened, what it means, and what experienced investors are doing right now. At Nova Capital, we operate with zero commission fees and no incentive to push you in either direction — only to give you the clearest picture of the market possible.",
      },
      {
        type: "h2",
        content: "What happened to Dubai's property market when the conflict escalated",
      },
      {
        type: "paragraph",
        content:
          "Between January and February 2026 alone, Dubai recorded AED 133.3 billion in real estate transactions across 34,452 deals. That momentum then hit a wall.",
      },
      {
        type: "paragraph",
        content:
          "The DFM Real Estate Index (DFMREI) — which tracks property developers listed on the Dubai Financial Market — plunged approximately 21% since the outbreak of the US-Israel-Iran conflict, falling from nearly 16,700 points to 13,353 by 9 March 2026.",
      },
      {
        type: "paragraph",
        content:
          "Buyer inquiries dropped by approximately 45% in the immediate days after the escalation. People paused. That is an honest number and it matters. But it is only half the picture.",
      },
      {
        type: "pullquote",
        content:
          "The emirate recorded 3,570 sales transactions between 2 and 9 March, with a total value of AED 11.93 billion — and the value of transactions rose over the last three days of that period.",
      },
      {
        type: "paragraph",
        content:
          "In other words: sentiment fell sharply, but actual transactions kept moving. That divergence is the most important signal in this market right now.",
      },
      {
        type: "h2",
        content: "The Q1 2026 data that most investors haven't seen",
      },
      {
        type: "paragraph",
        content:
          "While the conflict dominated headlines from late February onwards, the broader Q1 2026 picture tells a different story about structural demand.",
      },
      {
        type: "paragraph",
        content:
          "Q1 2026 data shows AED 252 billion in transaction volume, up 31% year-on-year, across 60,303 transactions — a 6% increase — with AED 148.35 billion in foreign investment, up 26%, and over 29,000 new investors, up 14%.",
      },
      {
        type: "paragraph",
        content:
          "These are not the numbers of a market in freefall. They are the numbers of a market that absorbed an initial shock and continued operating. Large capital is not exiting. New investors are entering. Demand is still there — but it is becoming more selective.",
      },
      {
        type: "paragraph",
        content:
          "The selectivity matters. Investors are not leaving Dubai — they are becoming more careful about what they buy, where, and at what price. That is rational behaviour, and it creates genuine opportunity for buyers who do their homework.",
      },
      {
        type: "h2",
        content: "Which segments are feeling pressure, and which are holding firm",
      },
      {
        type: "paragraph",
        content:
          "The conflict has not hit Dubai's property market evenly. Understanding the segment breakdown is critical for any investor making decisions right now.",
      },
      {
        type: "h3",
        content: "Off-plan and developer stocks — the hardest hit",
      },
      {
        type: "paragraph",
        content:
          "The off-plan market — which had been particularly buoyant in the months leading up to the conflict — faces a more uncertain outlook as buyers weigh the risks of committing capital to projects that may take years to complete. Developer stocks have been the most volatile. The Dubai real estate index had one of its worst weeks in history, shedding more than 15% in a single week, wiping out all of its 2026 gains.",
      },
      {
        type: "paragraph",
        content:
          "For <a href=\"/pricing\">off-plan buyers</a> with a 5–10 year horizon, this volatility is largely noise. For those planning to flip or exit within 12–18 months, it requires more careful positioning.",
      },
      {
        type: "h3",
        content: "Luxury real estate — under scrutiny but not collapsing",
      },
      {
        type: "paragraph",
        content:
          "One segment that could face an impact from a prolonged war is luxury real estate, according to S&P Global Ratings. The luxury segment could be hit as high-net-worth individuals and foreign investors reassess their investments and safe-haven status.",
      },
      {
        type: "paragraph",
        content:
          "Yet the data on the ground contradicts a full retreat. A luxury apartment in Dubai sold for AED 422 million in 2026, making it the third most expensive apartment sale in the emirate's history. Ultra-high-net-worth buyers are still transacting. They are simply being more deliberate about timing and asset selection.",
      },
      {
        type: "h3",
        content: "Mid-market residential — the most resilient",
      },
      {
        type: "paragraph",
        content:
          "Apartments in proven communities — JVC, Business Bay, Dubai Marina — have shown the strongest resilience. The median price per square foot has risen to AED 1,818 in 2026, almost twice AED 935 in 2020, and premium properties in high-demand  <a href=\"/areas\">areas remain in demand</a>, indicating that prime segments are not very exposed to geopolitical risks.",
      },
      {
        type: "paragraph",
        content:
          "Rental yields remain a key anchor. Rental yields in high-demand communities are between 6% and 8.5%, significantly higher than most cities in the world. As long as yields hold, income-focused investors have little reason to exit.",
      },
      {
        type: "h2",
        content: "Why Dubai's fundamentals have not changed",
      },
      {
        type: "paragraph",
        content:
          "The things that made Dubai attractive to international capital remain firmly in place.",
      },
      {
        type: "paragraph",
        content:
          "Dubai has no personal income tax, no capital gains tax, and no inheritance tax — making it ideal for the ultra wealthy and family offices. Those policies have not changed. The Golden Visa programme, which grants a 10-year renewable residency to property buyers investing above AED 2 million, has not changed. An estimated 9,800 millionaires moved to Dubai in 2025, bringing $63 billion in wealth — more than any other country in the world.",
      },
      {
        type: "callout",
        content:
          "<strong>Fitch Ratings</strong> has noted that banks and developers have strengthened their balance sheets, with real-estate loan exposure falling to 14% of UAE bank loans, meaning the system can withstand a correction.",
      },
      {
        type: "paragraph",
        content:
          "Dubai property market resilience is backed by strong banking liquidity, institutional crisis management capabilities, a diversified economic base, long-term urban development planning, and sustained international investor confidence.",
      },
      {
        type: "paragraph",
        content:
          "The structural case for Dubai — tax efficiency, population growth, global connectivity, and rule of law — has not been altered by the conflict. What has changed is the risk premium investors attach to the region, and that premium is now partly priced in.",
      },
      {
        type: "h2",
        content: "What smart investors are doing right now",
      },
      {
        type: "paragraph",
        content:
          "Experienced capital is not fleeing. It is repositioning. Some investors are reportedly \"doubling down\" on the basis that the structural case remains intact.",
      },
      {
        type: "paragraph",
        content:
          "Strong fundamentals carried into early 2026 from a period of record activity in late 2025. While analysts say the situation may temporarily slow transactions, many industry experts believe Dubai's property market fundamentals remain strong enough to absorb the shock, as it has during previous crises.",
      },
      {
        type: "paragraph",
        content:
          "Viewing activity showed a 75% increase in the last three days of the first week of conflict compared to the first three days — a clear sign that buyer and tenant confidence returned quickly.",
      },
      {
        type: "paragraph",
        content:
          "The investors taking meetings in March and April 2026 are the ones who will secure the best prices before confidence fully returns. Historically, every geopolitical shock to Dubai's market has been followed by a sharper-than-expected recovery.",
      },
      {
        type: "h2",
        content: "What could make things worse — and what to watch",
      },
      {
        type: "paragraph",
        content:
          "Honesty demands acknowledging the downside scenario. Fitch Ratings predicted that prices could fall as much as 15%, and the effect on real estate values will depend on the conflict's scope and duration.",
      },
      {
        type: "paragraph",
        content:
          "Tourism bookings have fluctuated, and insurance and finance sectors have begun pricing in higher risk premiums for Gulf exposure. Economists also warn that sustained geopolitical tension could deter broader capital inflows, especially from Asia and Europe, should uncertainty persist.",
      },
      {
        type: "paragraph",
        content:
          "The key variable is duration. A short, contained conflict produces a temporary pricing dip and a buying opportunity. A prolonged, escalating conflict produces sustained uncertainty and deeper corrections, particularly in luxury and off-plan segments. Experts suggest that if the conflict stabilises, investor confidence will quickly return, and international buyers may continue viewing Dubai as a safe destination for capital during regional instability.",
      },
      {
        type: "callout",
        content:
          "<strong>Signals to monitor:</strong> DLD weekly transaction volumes · Developer new-launch activity · DFMREI index trend. When all three turn upward together, the recovery window has opened.",
      },
    ],

    faqs: [
      {
        q: "Is Dubai real estate safe to invest in during the Iran-US war?",
        a: "Yes, with appropriate positioning. Q1 2026 DLD data shows AED 252 billion in transaction volume, up 31% year-on-year, with over 29,000 new investors entering the market. Short-term sentiment dipped, but structural demand — driven by tax advantages, Golden Visa incentives, and population growth — has not reversed.",
      },
      {
        q: "Have Dubai property prices dropped because of the conflict?",
        a: "Average home prices were reported modestly lower — off around 4–5% — in the early weeks following conflict escalation. This is a sentiment-driven correction, not a fundamental one. The median price per sqft has risen from AED 935 in 2020 to AED 1,818 in 2026. Analysts do not expect a dramatic collapse.",
      },
      {
        q: "Should I wait, or buy now?",
        a: "Waiting carries its own risk: a swift return of confidence pushes prices back up fast. The current window — where prices are modestly softer and competition is lower — is historically the window that long-term investors look back on favourably. That said, your decision should be based on your own horizon, risk tolerance, and liquidity — not headlines.",
      },
    ],
  },

  {
    slug: "india-oci-rule-change-may-2026-nri-property",
    html: HTML_BLOGS["india-oci-rule-change-may-2026-nri-property"],

    title:
      "India's New OCI Rules (May 2026): What NRIs & Expats Must Know Before Buying Property",

    excerpt:
      "India's Citizenship (Amendment) Rules 2026 took effect May 1, 2026 — the biggest OCI overhaul in 20 years. Here's what NRIs, OCI holders, and expat buyers need to know before investing in Indian real estate.",

    category: "NRI Property Guide",

    tag: "NRI Property Guide · May 2026",

    author: "Nova Editorial Team",

    datePublished: "2026-05-23T10:00:00+04:00",

    dateDisplay: "23 May 2026",

    readTime: "8 min read",

    image: "/images/blog/oci-rules-2026-og.jpg",

    imageAlt:
      "India OCI Rules 2026 — NRI Property Buyer's Guide",

    imageCaption:
      "India's Citizenship (Amendment) Rules, 2026 introduced the biggest OCI framework overhaul since 2005.",

    canonical:
      "https://www.novacapitaldxb.com/blog/india-oci-rule-change-may-2026-nri-property",

    metaDescription:
      "India's Citizenship (Amendment) Rules 2026 took effect May 1, 2026 — the biggest OCI overhaul in 20 years. Here's what NRIs, OCI holders, and expat buyers need to know before investing in Indian real estate.",

    keywords:
      "OCI rules 2026, India visa rule change May 2026, NRI property buying India 2026, OCI card property rights, NRI real estate India, OCI amendment rules, MHA OCI overhaul, India immigration 2026, NRI home buying guide",

    ogDescription:
      "India's biggest OCI overhaul in 20 years took effect May 1, 2026. Full digital regime, new fees, stricter compliance — and what it means if you're buying property in India.",

  },
  {
    slug: "dubai-property-investor-visa-2026-new-rules",
    html: HTML_BLOGS["dubai-property-investor-visa-2026-new-rules"],

    title:
      "Dubai Property Investor Visa 2026: No Minimum Value — New Rules Explained",

    excerpt: "Dubai scrapped the AED 750,000 property visa threshold on 1 May 2026. Any sole owner now qualifies for residency. Here's exactly how the new two-year investor visa works, who it affects, and how it compares to the Golden Visa.",
    category: "NRI Property Guide",

    tag: "NRI Property Guide · May 2026",

    author: "Nova Editorial Team",

    datePublished: "2026-05-26T10:00:00+04:00",

    dateDisplay: "26 May 2026",

    readTime: "8 min read",

    image: "/images/blog/dubai-investor-visa-2026-rules.jpg",

    imageAlt:
      "Dubai Land Department building with visa document overlay showing the May 2026 investor visa rule change",

    imageCaption:
      "Dubai Land Department confirmed the AED 750,000 threshold removal on 1 May 2026 via its Cube platform",

    canonical:
      "https://www.novacapitaldxb.com/blog/dubai-property-investor-visa-2026-new-rules",

    metaDescription:
      "Dubai scrapped the AED 750,000 property visa threshold on 1 May 2026. Any sole owner now qualifies for residency. Here's exactly how the new two-year investor visa works, who it affects, and how it compares to the Golden Visa.",

    keywords:
      "Dubai property investor visa 2026, Dubai investor visa no minimum, AED 750000 threshold removed, Dubai two year visa property, Dubai Golden Visa vs investor visa, Dubai residency property 2026, Nova Capital Dubai visa",

    ogDescription:
      "Dubai scrapped its AED 750,000 property visa floor on 1 May 2026. Any sole owner now qualifies for two-year residency — regardless of purchase price. Nova Capital breaks down every detail.",

  },
  {
    slug: "dubai-investor-visa-2026-no-minimum-property-value",
    html: HTML_BLOGS["dubai-investor-visa-2026-no-minimum-property-value"],

    title:
      "Dubai investor visa 2026: no minimum property value — complete guide for NRI and expat buyers",

    excerpt: "Dubai removed the AED 750,000 property minimum for the 2-year investor visa on 1 May 2026. Any sole owner now qualifies regardless of price. Rules, costs, documents, and best properties. Nova Capital — zero commission.",
    category: "Visa & Residency",

    tag: "Dubai investor visa, property visa UAE, NRI Dubai, expat property, Golden Visa, 2026",

    author: "Nova Editorial Team",

    datePublished: "2026-05-27T10:00:00+04:00",

    dateDisplay: "27 May 2026",

    readTime: "7 min read",

    image: "/images/blog/dubai-investor-visa-2026-no-minimum-property-value.jpg",

    imageAlt:
      "Dubai property investor visa 2026 — no minimum value — Nova Capital guide",

    imageCaption:
      "Dubai removed the AED 750,000 minimum for the 2-year property investor visa on 1 May 2026.",

    canonical:
      "https://www.novacapitaldxb.com/blog/dubai-investor-visa-2026-no-minimum-property-value",

    metaDescription:
      "Dubai just scrapped the AED 750,000 minimum for the 2-year property investor visa. Any sole owner now qualifies. Step-by-step guide for NRI and expat buyers. Nova Capital — zero commission, zero broker fees.",

    keywords:
      "Dubai investor visa 2026, no minimum property value Dubai, Dubai property visa NRI, AED 750000 threshold removed, buy Dubai apartment get residency, how to get Dubai residency property, Dubai 2 year investor visa guide, NRI Dubai property 2026.",

    ogDescription:
      "Dubai just scrapped the AED 750,000 minimum for the 2-year property investor visa. Any sole owner now qualifies. Step-by-step guide for NRI and expat buyers. Nova Capital — zero commission, zero broker fees.",

  }
];

export default BLOGS;
